import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, MapPinOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { DeviceCard } from '@/components/device/DeviceCard';
import { useFleetStore } from '@/stores/fleet';
import { geofencesApi } from '@/api/endpoints/geofences';
import { parseWkt, pointInArea, type ParsedArea } from '@/lib/wkt';
import {
  computeMachineStatus,
  STATUS_ORDER,
  type MachineStatus,
} from '@/lib/machineStatus';
import { extractUF } from '@/lib/address';
import { cn } from '@/lib/utils';

type StatusFilter = 'all' | MachineStatus;
type SortKey = 'updated' | 'name' | 'status';
type UfFilter = 'all' | 'unknown' | string;
type EquipFilter = 'all' | string;

const SHORT_LABEL: Record<MachineStatus, string> = {
  running: 'Ativas',
  ready: 'Prontas',
  blocked: 'Bloqueadas',
  off: 'Desligadas',
};

const EQUIP_LABEL: Record<string, string> = {
  estoque: 'Em estoque',
  instalado: 'Instalado',
  manutencao: 'Manutenção',
  rma: 'RMA',
  baixado: 'Baixado',
};

function deviceEquipStatus(d: { attributes?: Record<string, unknown> }): string {
  const v = d.attributes?.statusEquip;
  return typeof v === 'string' && v ? v : '';
}

export function DevicesPage() {
  const devices = useFleetStore((s) => s.devices);
  const positions = useFleetStore((s) => s.positions);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [ufFilter, setUfFilter] = useState<UfFilter>('all');
  const [equipFilter, setEquipFilter] = useState<EquipFilter>('all');
  const [onlyForaArea, setOnlyForaArea] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('updated');

  // Geocercas (para o filtro "fora da área"): mapa id -> área parseada.
  const geofencesQ = useQuery({ queryKey: ['geofences'], queryFn: () => geofencesApi.list() });
  const areaById = useMemo(() => {
    const m = new Map<number, ParsedArea>();
    for (const g of geofencesQ.data ?? []) {
      const a = parseWkt(g.area);
      if (a) m.set(g.id, a);
    }
    return m;
  }, [geofencesQ.data]);

  const enriched = useMemo(() => {
    return Array.from(devices.values()).map((device) => {
      const position = positions.get(device.id);
      const status = computeMachineStatus(device, position);
      const lastUpdateMs = position?.fixTime
        ? new Date(position.fixTime).getTime()
        : device.lastUpdate
          ? new Date(device.lastUpdate).getTime()
          : 0;
      const uf = extractUF(position?.address);
      // "fora da área": tem geocerca(s) atribuída(s) e a posição não está dentro de nenhuma.
      const assigned = device.geofenceIds ?? [];
      let foraArea = false;
      if (assigned.length > 0 && position) {
        const inside = assigned.some((gid) => {
          const a = areaById.get(gid);
          return a ? pointInArea(position.longitude, position.latitude, a) : false;
        });
        foraArea = !inside;
      }
      return { device, position, status, lastUpdateMs, uf, foraArea };
    });
  }, [devices, positions, areaById]);

  const counts = useMemo(() => {
    const c: Record<MachineStatus, number> = {
      running: 0,
      ready: 0,
      blocked: 0,
      off: 0,
    };
    for (const e of enriched) c[e.status] += 1;
    return c;
  }, [enriched]);

  const ufOptions = useMemo(() => {
    const map = new Map<string, number>();
    let unknownCount = 0;
    for (const e of enriched) {
      if (e.uf) {
        map.set(e.uf, (map.get(e.uf) ?? 0) + 1);
      } else {
        unknownCount += 1;
      }
    }
    const list = Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    return { list, unknownCount };
  }, [enriched]);

  const equipOptions = useMemo(() => {
    const map = new Map<string, number>();
    let unknownCount = 0;
    for (const e of enriched) {
      const s = deviceEquipStatus(e.device);
      if (s) map.set(s, (map.get(s) ?? 0) + 1);
      else unknownCount += 1;
    }
    return { list: Array.from(map.entries()), unknownCount };
  }, [enriched]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = enriched;
    if (q) {
      list = list.filter(
        (e) =>
          e.device.name.toLowerCase().includes(q) ||
          e.device.uniqueId.toLowerCase().includes(q),
      );
    }
    if (statusFilter !== 'all') {
      list = list.filter((e) => e.status === statusFilter);
    }
    if (ufFilter === 'unknown') {
      list = list.filter((e) => !e.uf);
    } else if (ufFilter !== 'all') {
      list = list.filter((e) => e.uf === ufFilter);
    }
    if (equipFilter === 'unknown') {
      list = list.filter((e) => !deviceEquipStatus(e.device));
    } else if (equipFilter !== 'all') {
      list = list.filter((e) => deviceEquipStatus(e.device) === equipFilter);
    }
    if (onlyForaArea) {
      list = list.filter((e) => e.foraArea);
    }
    list = [...list].sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return a.device.name.localeCompare(b.device.name, 'pt-BR');
        case 'status':
          return STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
        case 'updated':
        default:
          return b.lastUpdateMs - a.lastUpdateMs;
      }
    });
    return list;
  }, [enriched, search, statusFilter, ufFilter, equipFilter, onlyForaArea, sortKey]);

  const foraAreaCount = useMemo(() => enriched.filter((e) => e.foraArea).length, [enriched]);

  if (devices.size === 0) {
    return <div className="p-6 text-muted-foreground">Carregando embarcações…</div>;
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar por nome ou ID único"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <Chip
          label={`Todos · ${enriched.length}`}
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
        />
        {STATUS_ORDER.map((s) => (
          <Chip
            key={s}
            label={`${SHORT_LABEL[s]} · ${counts[s]}`}
            active={statusFilter === s}
            onClick={() => setStatusFilter(s)}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <UfSelect
          value={ufFilter}
          onChange={setUfFilter}
          options={ufOptions.list}
          unknownCount={ufOptions.unknownCount}
          totalCount={enriched.length}
        />
        {ufFilter !== 'all' && (
          <button
            type="button"
            onClick={() => setUfFilter('all')}
            className="text-xs font-medium text-primary hover:underline"
          >
            Limpar UF
          </button>
        )}
        <EquipSelect
          value={equipFilter}
          onChange={setEquipFilter}
          options={equipOptions.list}
          unknownCount={equipOptions.unknownCount}
          totalCount={enriched.length}
        />
        <button
          type="button"
          onClick={() => setOnlyForaArea((v) => !v)}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold',
            onlyForaArea
              ? 'border-status-blocked bg-status-blocked/10 text-status-blocked'
              : 'border-border text-muted-foreground',
          )}
        >
          <MapPinOff className="h-3.5 w-3.5" />
          Fora da área · {foraAreaCount}
        </button>
        <select
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value as SortKey)}
          className="ml-auto h-8 rounded-md border border-input bg-background px-2 text-xs"
        >
          <option value="updated">Mais recente</option>
          <option value="name">Nome</option>
          <option value="status">Status</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Nenhuma embarcação corresponde aos filtros.
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {filtered.map(({ device, position }) => (
            <DeviceCard key={device.id} device={device} position={position} />
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-all',
        active
          ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {label}
    </button>
  );
}

interface UfSelectProps {
  value: UfFilter;
  onChange: (v: UfFilter) => void;
  options: [string, number][];
  unknownCount: number;
  totalCount: number;
}

function UfSelect({ value, onChange, options, unknownCount, totalCount }: UfSelectProps) {
  const active = value !== 'all';
  return (
    <label
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold',
        active ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground',
      )}
    >
      <span>UF</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="cursor-pointer bg-transparent outline-none"
      >
        <option value="all">Todas · {totalCount}</option>
        {options.map(([uf, count]) => (
          <option key={uf} value={uf}>
            {uf} · {count}
          </option>
        ))}
        {unknownCount > 0 && <option value="unknown">Indefinida · {unknownCount}</option>}
      </select>
    </label>
  );
}

interface EquipSelectProps {
  value: EquipFilter;
  onChange: (v: EquipFilter) => void;
  options: [string, number][];
  unknownCount: number;
  totalCount: number;
}

function EquipSelect({ value, onChange, options, unknownCount, totalCount }: EquipSelectProps) {
  const active = value !== 'all';
  return (
    <label
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold',
        active ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground',
      )}
    >
      <span>Equip.</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="cursor-pointer bg-transparent outline-none"
      >
        <option value="all">Todos · {totalCount}</option>
        {options.map(([s, count]) => (
          <option key={s} value={s}>
            {EQUIP_LABEL[s] ?? s} · {count}
          </option>
        ))}
        {unknownCount > 0 && <option value="unknown">Sem status · {unknownCount}</option>}
      </select>
    </label>
  );
}
